import { useEffect, useState } from "react";
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Token as TokenIcon,
  People as PeopleIcon,
  TrackChanges as IntentIcon,
  Speed as PerformanceIcon,
  Description as ReportIcon,
  Refresh as RefreshIcon,
  ExpandMore,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import OverviewTab from "./OverviewTab";
import TokenUsageTab from "./TokenUsageTab";
import UserBehaviorTab from "./UserBehaviorTab";
import IntentAnalysisTab from "./IntentAnalysisTab";
import PerformanceTab from "./PerformanceTab";
import DetailedReportsTab from "./DetailedReportsTab";
import { validateDateRange } from "./validation";
import ButtonOne from "../../../common/normal-button";
import CustomTabs from "../../../common/custom-tabs";
import { ColorsConstants } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import AppDropdown from "../../../common/app-dropdown";
import AppDatePicker from "../../../common/app-date-picker";

const AIBotReport = () => {
  const [activeTab, setActiveTab] = useState(0);

  const [period, setPeriod] = useState("7d");
  const [startDate, setStartDate] = useState("2024-01-01");
  const [endDate, setEndDate] = useState("2024-01-07");
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleRefresh = () => {
    try {
      const validation = validateDateRange(startDate, endDate);
      if (!validation.isValid) {
        setErrors(validation.errors);
        setSnackbar({
          open: true,
          message: "Please fix validation errors",
          severity: "error",
        });
        return;
      }
      setErrors({});
      setSnackbar({
        open: true,
        message: "Dashboard refreshed!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error:", error);
      setSnackbar({
        open: true,
        message: "Error refreshing dashboard",
        severity: "error",
      });
    }
  };

  const getDateRangeFromPeriod = (period) => {
    const today = new Date();
    const end = new Date(today);
    const start = new Date(today);

    switch (period) {
      case "24h":
        start.setDate(today.getDate() - 1);
        break;
      case "7d":
        start.setDate(today.getDate() - 7);
        break;
      case "30d":
        start.setDate(today.getDate() - 30);
        break;
      case "90d":
        start.setDate(today.getDate() - 90);
        break;
      default:
        break;
    }

    const format = (d) => d.toISOString().split("T")[0];

    return {
      startDate: format(start),
      endDate: format(end),
    };
  };

  useEffect(() => {
    const { startDate, endDate } = getDateRangeFromPeriod(period);
    setStartDate(startDate);
    setEndDate(endDate);
  }, []);

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  // Custom tabs configuration
  const tabs = [
    { label: "Overview", value: 0 },
    { label: "Token Usage", value: 1 },
    { label: "User Behavior", value: 2 },
    { label: "Intent Analysis", value: 3 },
    { label: "Reports", value: 4 },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Period options for dropdown
  const periodOptions = [
    { label: "Last 24 Hours", value: "24h" },
    { label: "Last 7 Days", value: "7d" },
    { label: "Last 30 Days", value: "30d" },
    { label: "Last 90 Days", value: "90d" },
    { label: "Custom", value: "custom" },
  ];

  // Paper styling
  const paperStyle = {
    borderRadius: "8px",
    boxShadow: "0px 1px 5px 0px #0000001A",
    p: 3,
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh", bgcolor: "#FAFAFA" }}>
      <Container maxWidth="xl" sx={{ pt: 3, mb: 4 }}>
        {/* Filter Paper */}
        <Paper elevation={0} sx={{ ...paperStyle, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              width: "100%",
              gap: 2,
            }}
          >
            {/* Period Select - using AppDropdown */}
            <Box sx={{ flex: "1 1 200px", minWidth: "150px" }}>
              <Typography 
                sx={{ 
                  ...typography.bodySmall, 
                  color: "#6C7C93", 
                  mb: 0.5,
                  ml: 0.5
                }}
              >
                Period
              </Typography>
              <AppDropdown
                menuItems={periodOptions}
                selectedValue={period}
                onMenuSelect={(value) => {
                  const selectedPeriod = value;
                  setPeriod(selectedPeriod);

                  if (selectedPeriod !== "custom") {
                    const { startDate, endDate } =
                      getDateRangeFromPeriod(selectedPeriod);
                    setStartDate(startDate);
                    setEndDate(endDate);
                  }
                }}
                variant="outlined"
                endIcon={<ExpandMore />}
                sx={{
                  width: "100%",
                  height: 38,
                  textAlign: "left",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ ...typography.bodyLarge }}>
                  {periodOptions.find(opt => opt.value === period)?.label || "Select Period"}
                </span>
              </AppDropdown>
            </Box>

            {/* Start Date - using AppDatePicker */}
            <Box sx={{ flex: "1 1 250px", minWidth: "180px" }}>
              <Typography 
                sx={{ 
                  ...typography.bodySmall, 
                  color: "#6C7C93", 
                  mb: 0.5,
                  ml: 0.5
                }}
              >
                Start Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <AppDatePicker
                  format="dd/MM/yyyy"
                  value={startDate ? new Date(startDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const formattedDate = newValue.toISOString().split('T')[0];
                      setStartDate(formattedDate);
                      setPeriod("custom");
                    }
                  }}
                  minDate={null}
                  disableFuture={false}
                  placeholder="Start Date"
                  fullWidth
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />
              </LocalizationProvider>
            </Box>

            {/* End Date - using AppDatePicker */}
            <Box sx={{ flex: "1 1 250px", minWidth: "180px" }}>
              <Typography 
                sx={{ 
                  ...typography.bodySmall, 
                  color: "#6C7C93", 
                  mb: 0.5,
                  ml: 0.5
                }}
              >
                End Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <AppDatePicker
                  format="dd/MM/yyyy"
                  value={endDate ? new Date(endDate) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      const formattedDate = newValue.toISOString().split('T')[0];
                      setEndDate(formattedDate);
                      setPeriod("custom");
                    }
                  }}
                  minDate={startDate ? new Date(startDate) : null}
                  disableFuture={false}
                  placeholder="End Date"
                  fullWidth
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                />
              </LocalizationProvider>
            </Box>


            {/* Refresh Button - auto width */}
            <Box sx={{ flex: "0 0 auto", alignSelf: "flex-end", ml: "auto" }}>
              <ButtonOne
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                title="Refresh"
                sx={{
                  height: 38,
                  ...typography.bodyLarge,
                  backgroundColor: ColorsConstants.primaryBlue100,
                }}
              >
                Refresh
              </ButtonOne>
            </Box>

            {/* Error Alert - full width */}
            {errors.dateRange && (
              <Box sx={{ flex: "1 1 100%", mt: 1 }}>
                <Alert severity="error" sx={{ width: "100%", "& .MuiAlert-message": typography.bodyLarge }}>
                  {errors.dateRange}
                </Alert>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Tabs Section */}
        <CustomTabs value={activeTab} onChange={handleTabChange} tabs={tabs} />

        {/* Content Panels */}
        <TabPanel value={activeTab} index={0}>
          <OverviewTab startDate={startDate} endDate={endDate} />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <TokenUsageTab startDate={startDate} endDate={endDate} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <UserBehaviorTab startDate={startDate} endDate={endDate} />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <IntentAnalysisTab startDate={startDate} endDate={endDate} />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <DetailedReportsTab startDate={startDate} endDate={endDate} />
        </TabPanel>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          sx={{ "& .MuiAlert-message": typography.bodyLarge }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIBotReport;